/**
 * The pay page opens in a NEW tab, and this tab waits for it (patient
 * payments, slice 3a; decided after the review of PR #453).
 *
 * The pay page lives on its own PHI-free origin; this tab keeps the decrypted
 * form in memory, so it is never navigated away. `openPayPage` opens the new
 * window SYNCHRONOUSLY inside the click (before any await — a window opened
 * after an await is a blocked popup), then runs the server calls, then points
 * the window at the pay URL with `opener` cut. `PaymentWaiting` polls the
 * status every 3 s for up to 10 minutes and hands back the row once it reads
 * `card_saved` (or `paid`, for a pay-now). When the browser blocked the popup
 * it shows a plain link to the pay page instead, with the same waiting view.
 *
 * "I've saved my card" first asks the bridge to check with Stripe at once
 * (`POST …/refresh`, decision G5 of the 2026-10-14 build), so a card whose
 * webhook is late is found on the tap rather than left polling. The second
 * button is the caller's: "Back" on a row without the no-fee choices (it
 * returns to the two choices and never defers), "Continue without paying
 * now" on a row that has them, "Not now" on the status page.
 */
import React, { useEffect, useRef, useState } from "react";

import { J } from "../../shared/ui/journey/journeyTheme";
import { fetchPaymentStatus, postPaymentRefresh } from "../../lib/bridgeClient";
import type { PatientPaymentStatus } from "../../types/shared";

export const POLL_MS = 3000;
export const POLL_MAX_MS = 10 * 60 * 1000;

export interface PayPageOpened {
  payUrl: string;
  /** The browser blocked the popup: show a link instead. */
  popupBlocked: boolean;
}

/**
 * Must be called synchronously from a click handler. `prepare` runs the
 * server calls (consent, setup) and returns the pay URL, or throws.
 */
export async function openPayPage(prepare: () => Promise<string>): Promise<PayPageOpened> {
  let w: Window | null = null;
  try {
    w = window.open("about:blank", "_blank");
  } catch {
    w = null;
  }
  let payUrl: string;
  try {
    payUrl = await prepare();
  } catch (e) {
    try {
      if (w) w.close();
    } catch {
      /* ignore */
    }
    throw e;
  }
  if (w) {
    try {
      w.opener = null;
      w.location.href = payUrl;
      return { payUrl, popupBlocked: false };
    } catch {
      /* fall through to the link */
    }
  }
  return { payUrl, popupBlocked: true };
}

const DONE_STATUSES = ["card_saved", "paid"];

const primary: React.CSSProperties = {
  width: "100%",
  minHeight: 50,
  border: 0,
  borderRadius: J.radiusButton,
  background: J.ink,
  color: J.white,
  fontSize: 16,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};
const secondary: React.CSSProperties = { ...primary, background: J.white, color: J.ink, border: `1px solid ${J.border}` };

export default function PaymentWaiting(props: {
  opened: PayPageOpened;
  /** The row reached card_saved (or paid). */
  onSaved: (payment: PatientPaymentStatus) => void;
  /** The second button: "Back" on the two-choice fee step (never a
   *  deferral), "Continue without paying now" on a row sent with the no-fee
   *  choices, "Not now" on the status page. */
  leaveLabel: string;
  onLeave: () => void;
  pollMs?: number;
  maxMs?: number;
}) {
  const { opened, onSaved, leaveLabel, onLeave } = props;
  const pollMs = props.pollMs ?? POLL_MS;
  const maxMs = props.maxMs ?? POLL_MAX_MS;
  const [note, setNote] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const doneRef = useRef(false);
  const savedRef = useRef(onSaved);
  savedRef.current = onSaved;

  async function check(): Promise<boolean> {
    const r = await fetchPaymentStatus();
    if (doneRef.current) return true;
    if (r.kind === "ok" && DONE_STATUSES.includes(String(r.payment.status))) {
      doneRef.current = true;
      savedRef.current(r.payment);
      return true;
    }
    return false;
  }

  useEffect(() => {
    const started = Date.now();
    const id = window.setInterval(() => {
      if (doneRef.current) return;
      if (Date.now() - started > maxMs) {
        window.clearInterval(id);
        setTimedOut(true);
        return;
      }
      void check();
    }, pollMs);
    return () => {
      doneRef.current = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function savedClicked() {
    if (busy) return;
    setBusy(true);
    // Ask Stripe now (the bridge reconciles a completed setup session whose
    // webhook has not arrived); its answer is advisory, the status read
    // below decides. A failure (rate limit, network) changes nothing.
    try {
      await postPaymentRefresh();
    } catch {
      /* never fails the tap */
    }
    const ok = await check();
    if (!ok) {
      setBusy(false);
      setNote("We haven't heard from the payment page yet. If you finished there, give it a moment and try again.");
    }
  }

  return (
    <section aria-labelledby="pay-wait-heading" data-testid="payment-waiting">
      <h1 id="pay-wait-heading" tabIndex={-1} style={{ fontSize: 22, color: J.ink, margin: "6px 0 8px" }}>
        Waiting for your card…
      </h1>
      <p style={{ fontSize: 15, color: J.body, lineHeight: 1.5, margin: "0 0 12px" }}>
        {opened.popupBlocked
          ? "Your browser didn't open the payment page. Open it with the link below; this page continues once your card is saved."
          : "The payment page opened in a new tab. Finish there, then come back to this tab; it continues once your card is saved."}
      </p>
      {opened.popupBlocked ? (
        <p style={{ margin: "0 0 14px" }}>
          <a href={opened.payUrl} target="_blank" rel="noopener noreferrer" style={{ color: J.teal, fontWeight: 600 }}>
            Open the payment page
          </a>
        </p>
      ) : null}
      <div aria-live="polite" role="status">
        {note ? <p style={{ fontSize: 14, color: J.body, margin: "0 0 10px" }}>{note}</p> : null}
        {timedOut ? (
          <p style={{ fontSize: 14, color: J.body, margin: "0 0 10px" }}>
            We stopped checking automatically. Use the button below once your card is saved.
          </p>
        ) : null}
      </div>
      <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
        <button type="button" style={primary} disabled={busy} onClick={savedClicked}>
          I've saved my card
        </button>
        <button type="button" style={secondary} onClick={onLeave}>
          {leaveLabel}
        </button>
      </div>
    </section>
  );
}
